import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Phone, MoreHorizontal, Plus, Mail, Building2 } from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { useWithBack } from "@/lib/nav";
import StatusBadge from "@/components/StatusBadge";
import CallLogDialog from "@/components/CallLogDialog";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const WINDOWS = [
  { value: "60", label: "60+ days since last service" },
  { value: "90", label: "90+ days since last service" },
  { value: "180", label: "180+ days since last service" },
];

export default function CallToBookPage() {
  const withBackHref = useWithBack();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [withinDays, setWithinDays] = useState("90");
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeCustomer, setActiveCustomer] = useState(null);
  const reqIdRef = useRef(0);

  const load = useCallback(async () => {
    const myReqId = ++reqIdRef.current;
    setLoading(true);
    try {
      const { data } = await api.get("/service/call-to-book", {
        params: { within_days: Number(withinDays) },
      });
      if (myReqId !== reqIdRef.current) return;
      setItems(data.items || []);
    } catch (err) {
      if (myReqId !== reqIdRef.current) return;
      toast.error(formatApiError(err));
    } finally {
      if (myReqId === reqIdRef.current) setLoading(false);
    }
  }, [withinDays]);

  useEffect(() => { load(); }, [load]);

  const openLog = (row) => { setActiveCustomer(row); setDialogOpen(true); };

  const createJob = (row) => {
    const params = new URLSearchParams({ customer_id: row.customer_id || "" });
    navigate(`/jobs/new?${params.toString()}`);
  };

  return (
    <div className="p-6 space-y-6" data-testid="call-to-book-page">
      <PageHeader
        testId="call-to-book-header"
        title="Call to Book"
        subtitle="On-demand customers who haven't been serviced in a while."
      />

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={withinDays} onValueChange={setWithinDays}>
            <SelectTrigger className="h-8 w-64 text-xs" data-testid="call-to-book-window">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {WINDOWS.map((w) => (<SelectItem key={w.value} value={w.value}>{w.label}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div className="text-xs text-muted-foreground">
          {loading ? "Loading…" : `${items.length} customer${items.length === 1 ? "" : "s"}`}
        </div>
      </div>

      <Card className="rounded-md overflow-hidden">
        <CardContent className="p-0">
          <table className="w-full text-sm" data-testid="call-to-book-table">
            <thead className="bg-muted/50 border-b border-border">
              <tr className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                <th className="text-left py-2 px-3">Customer</th>
                <th className="text-left py-2 px-3">Contact</th>
                <th className="text-left py-2 px-3">Machines</th>
                <th className="text-left py-2 px-3">Last serviced</th>
                <th className="text-right py-2 px-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="py-8 text-center text-xs text-muted-foreground">Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={5} className="py-8 text-center text-xs text-muted-foreground" data-testid="call-to-book-empty">
                  No on-demand customers need a call right now.
                </td></tr>
              ) : items.map((row) => (
                <tr key={row.customer_id} data-testid={`call-to-book-row-${row.customer_id}`} className="border-b border-border last:border-0 hover:bg-muted/30">
                  <td className="py-2 px-3">
                    <Link to={withBackHref(`/customers/${row.customer_id}`)} className="font-medium hover:text-primary flex items-center gap-2" data-testid={`call-to-book-customer-link-${row.customer_id}`}>
                      <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                      {row.customer_name}
                    </Link>
                    {row.notes && (
                      <div className="text-xs text-muted-foreground truncate max-w-md" title={row.notes}>{row.notes}</div>
                    )}
                  </td>
                  <td className="py-2 px-3 text-muted-foreground">
                    {(() => {
                      const contacts = row.contacts || [];
                      const primary = contacts.find((c) => c.role === "Primary") || contacts[0] || {};
                      const extra = contacts.length > 1 ? contacts.length - 1 : 0;
                      return (
                        <>
                          <div className="flex items-center gap-2">
                            <span>{primary.name || "—"}</span>
                            {extra > 0 && (
                              <span
                                className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground border border-border rounded-sm px-1.5 py-0.5"
                                data-testid={`call-to-book-contact-extra-${row.customer_id}`}
                              >
                                +{extra} more
                              </span>
                            )}
                          </div>
                          {primary.phone && (
                            <div className="text-xs flex items-center gap-1"><Phone className="h-3 w-3" /> {primary.phone}</div>
                          )}
                          {primary.email && (
                            <div className="text-xs flex items-center gap-1"><Mail className="h-3 w-3" /> {primary.email}</div>
                          )}
                        </>
                      );
                    })()}
                  </td>
                  <td className="py-2 px-3">
                    <StatusBadge tone={row.machines_count > 0 ? "info" : "muted"}>{row.machines_count} machine{row.machines_count === 1 ? "" : "s"}</StatusBadge>
                  </td>
                  <td className="py-2 px-3">
                    {row.last_completed_at ? (
                      <div>
                        <div>{row.last_completed_at}</div>
                        <div className="text-xs text-muted-foreground">{row.days_since_last}d ago</div>
                      </div>
                    ) : (
                      <StatusBadge tone="warning">Never</StatusBadge>
                    )}
                  </td>
                  <td className="py-2 px-3 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`call-to-book-actions-${row.customer_id}`}>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openLog(row)} data-testid={`call-to-book-log-${row.customer_id}`}>
                          <Phone className="h-4 w-4 mr-2" /> Log call outcome
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => createJob(row)} data-testid={`call-to-book-create-job-${row.customer_id}`}>
                          <Plus className="h-4 w-4 mr-2" /> Create Job
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => navigate(`/customers/${row.customer_id}`)} data-testid={`call-to-book-view-${row.customer_id}`}>
                          <Building2 className="h-4 w-4 mr-2" /> View customer
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <CallLogDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        customer={activeCustomer}
        onLogged={() => load()}
      />
    </div>
  );
}
